import React, { useState, useRef, memo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Users, Heart, Globe, GraduationCap, School, Calendar, Eye, Layers, Compass, Sparkles, MessageCircle, ChevronRight, Building2, ShieldCheck, Mail, Send } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// ---------- Shared visual primitives ----------
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-6912" data-mp="2t24tvv1o1o8m">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
  align?: 'center' | 'left';
}) => <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`} data-id="element-6913" data-mp="2u4gpjzsas90w">
    <Eyebrow data-id="element-6914" data-mp="2yyb9937wknzz">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-6915" data-mp="2yyl1yykc199d">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-6916" data-mp="2p78bobe6mr5g">{highlight}</span>
        </>}
    </h2>
    <div className={`w-16 h-px bg-amber-400/30 mb-6 ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-6917" data-mp="3cx2j2dfqfnib" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-6918" data-mp="3htjgjy8tne82">
        {intro}
      </p>}
  </div>;
const PremiumCard = ({
  icon: Icon,
  title,
  body,
  highlight = false
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  highlight?: boolean;
}) => <div className={`group relative border ${highlight ? 'border-amber-400/40' : 'border-amber-400/15'} bg-gradient-to-b from-amber-950/10 to-transparent hover:border-amber-400/50 transition-colors duration-500 p-6 sm:p-7`} data-id="element-6919" data-mp="1rlj6g6zu7t58">
    <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-6920" data-mp="3u5llpputjax2" />
    <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-5" data-id="element-6921" data-mp="3pdu2y1hwm9hh">
      <Icon className="w-4 h-4 text-amber-400" data-id="element-6922" data-mp="3kfsawr2i2ztv" />
    </div>
    <h3 className="font-serif text-lg sm:text-xl text-white mb-3 leading-tight" data-id="element-6923" data-mp="3fhh3so3lb3fc">
      {title}
    </h3>
    <p className="text-sm sm:text-[15px] text-white/60 leading-relaxed font-light" data-id="element-6924" data-mp="3bf470p6tmfup">
      {body}
    </p>
  </div>;
// =====================================================================
// 1. HERO
// =====================================================================
const SchoolsHero = () => <section className="relative w-full min-h-[92vh] bg-black flex items-center justify-center overflow-hidden pt-20" data-id="element-6925" data-mp="14p65eaot86ey">
    <div className="absolute inset-0" data-id="element-6926" data-mp="zr4lxonh252y">
      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-25" data-id="element-6927" data-mp="zo0b48fi4b3b" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" data-id="element-6928" data-mp="1gzf6murkvwzn" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-6929" data-mp="1lxyvp41gxow8" />
    </div>

    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-6930" data-mp="oc19ownvr1fj">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.1
    }} data-id="element-6931" data-mp="tam9ww34tl3z">
        <Link to="/" className="inline-flex items-center gap-2 text-amber-400/60 hover:text-amber-400 text-xs tracking-[0.25em] uppercase font-light mb-12 transition-colors" data-id="element-6932" data-mp="135nztu58er7m">
          <ArrowLeft className="w-3.5 h-3.5" data-id="element-6933" data-mp="2eoqpxvyz43gx" />
          Back to Life in the Sikh Kingdoms
        </Link>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 0.25
    }} data-id="element-6934" data-mp="286xbb6wxchsf">
        <p className="text-amber-400 tracking-[0.35em] text-[10px] sm:text-xs uppercase mb-7 font-light" data-id="element-6935" data-mp="2h5r2uiveehz4">
          A School &amp; Curriculum Partnership Opportunity
        </p>
        <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-white mb-7 tracking-tight leading-[0.95]" data-id="element-6936" data-mp="2r6b5npykfujg">
          For Schools &amp;
          <br data-id="element-6937" data-mp="2zxto67izimfp" />
          <span className="text-amber-400" data-id="element-6938" data-mp="2zy3c7pkgyjir">Curriculum Partners</span>
        </h1>
        <div className="w-24 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-6939" data-mp="3ev3wj80bzc77" />
        <p className="text-lg sm:text-xl md:text-2xl text-white/75 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-6940" data-mp="3evlz0mdl6mnk">
          An immersive Sikh history learning experience that supports identity,
          belonging, cultural understanding, and student engagement.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.55
    }} className="mt-10 max-w-3xl mx-auto" data-id="element-6941" data-mp="1dgveru3rqop">
        <p className="text-sm sm:text-base text-white/55 font-light leading-relaxed" data-id="element-6942" data-mp="39t0krfrnkoyh">
          Life of the Sikh Kingdoms brings Sikh history to life through
          immersive storytelling, visual world-building, and culturally
          meaningful learning. It offers schools a modern way to help middle
          school and high school students connect with history, heritage, and
          identity in a format that feels vivid, memorable, and relevant.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.75
    }} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-6943" data-mp="2vszibvvqp9gw">
        <a href="#interest-form" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.25)]" data-id="element-6944" data-mp="22xi9qw7qgt1z">
          Request a Conversation
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6945" data-mp="2lpu7f5nfz65s" />
        </a>
        <a href="#program" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-6946" data-mp="2griyqy8vv1p5">
          Explore the School Program
        </a>
      </motion.div>
    </div>

    <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" animate={{
    y: [0, 10, 0]
  }} transition={{
    duration: 2,
    repeat: Infinity
  }} data-id="element-6947" data-mp="3gx4m7yxty561">
      <div className="w-px h-12 bg-gradient-to-b from-transparent via-amber-400/60 to-transparent" data-id="element-6948" data-mp="3qvkbrxh7l6df" />
    </motion.div>
  </section>;
// =====================================================================
// 2. WHY THIS MATTERS
// =====================================================================
const WhyThisMattersSection = () => {
  const cards = [{
    icon: Heart,
    title: 'Identity & Belonging',
    body: 'A space where Punjabi Sikh students see their history, heritage, and people centred in a way that feels alive and contemporary.'
  }, {
    icon: BookOpen,
    title: 'Sikh History Made Meaningful',
    body: 'A visual, story-led entry point into Sikh history that moves beyond textbooks and feels relevant to today\u2019s students.'
  }, {
    icon: Globe,
    title: 'Cultural Understanding for All Students',
    body: 'For non-Sikh students, a thoughtful introduction to Sikh history, culture, and the communities they share classrooms with.'
  }];
  return <section id="program" className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6949" data-mp="ur9t0nguolll">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/5 to-transparent pointer-events-none" data-id="element-6950" data-mp="71fg65wj6x1c" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-6951" data-mp="2479b1gu2ffe">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6952" data-mp="h0espzkf3ctm">
          <SectionHeading eyebrow="The Case for Schools" title="Why This" highlight="Matters" intro="Many students encounter history as something distant, abstract, or disconnected from their lives. Life of the Sikh Kingdoms offers a more meaningful entry point — deepening heritage and representation for Punjabi Sikh students, and broadening cultural understanding for everyone in the classroom." data-id="element-6953" data-mp="1nfffbenjyinj" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto" data-id="element-6954" data-mp="2dpzdnji84k1u">
          {cards.map((c, i) => <motion.div key={c.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} data-id="element-6955" data-mp="2ioizzl0qbxuq" data-mpi={i}>
              <PremiumCard {...c} data-id="element-6956" data-mp="31i6eauuzfd72" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 3. WHO IT SERVES
// =====================================================================
const WhoItServesSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6957" data-mp="9gz05bpe2xqr">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6958" data-mp="maxzuf41w66">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="text-center max-w-4xl mx-auto mb-14" data-id="element-6959" data-mp="kq3g4ssqd8b">
        <Eyebrow data-id="element-6960" data-mp="3rq9b8wrolscj">Who It Serves</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-6961" data-mp="3dskv228k79dt">
          Especially Meaningful for Punjabi Sikh Students —{' '}
          <span className="text-amber-400" data-id="element-6962" data-mp="2z5m4e9hj0j05">Valuable for All Students</span>
        </h2>
        <div className="w-16 h-px bg-amber-400/30 mx-auto" data-id="element-6963" data-mp="2u7as7s3rdwer" />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-6964" data-mp="2u9drz3upr3wo">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 to-transparent p-8 sm:p-10" data-id="element-6965" data-mp="3883s6sctxjp9">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-6966" data-mp="3mvovondm9kxp" />
          <div className="flex items-center gap-3 mb-5" data-id="element-6967" data-mp="4r52a1mpj12v">
            <div className="w-10 h-10 border border-amber-400/50 bg-amber-400/10 flex items-center justify-center" data-id="element-6968" data-mp="4nr2901in3yl">
              <Heart className="w-4 h-4 text-amber-400" data-id="element-6969" data-mp="2tj8mag8a5i2s" />
            </div>
            <Eyebrow data-id="element-6970" data-mp="2th5kkyxrm0i8">Heritage Centred</Eyebrow>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl text-amber-400 mb-4 leading-tight" data-id="element-6971" data-mp="2xlddz9f644ms">
            For Punjabi Sikh Students
          </h3>
          <p className="text-white/70 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6972" data-mp="37adckrprcy0g">
            Supports stronger connection to heritage, identity, pride, and
            cultural memory through a format that feels immersive and alive —
            history they can see themselves inside.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="relative border border-amber-400/15 bg-gradient-to-br from-white/[0.02] to-transparent p-8 sm:p-10" data-id="element-6973" data-mp="3l9wm9oy7whev">
          <div className="flex items-center gap-3 mb-5" data-id="element-6974" data-mp="1odihxoeijhv5">
            <div className="w-10 h-10 border border-white/20 bg-white/5 flex items-center justify-center" data-id="element-6975" data-mp="1jk77ldahsiyh">
              <Globe className="w-4 h-4 text-white/80" data-id="element-6976" data-mp="1emffoj9ukwua" />
            </div>
            <Eyebrow data-id="element-6977" data-mp="19nkpgw9jw605">Broader Learning</Eyebrow>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4 leading-tight" data-id="element-6978" data-mp="19kyj2zyebwji">
            For All Students
          </h3>
          <p className="text-white/65 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6979" data-mp="22b70uqp0sexk">
            Builds cultural understanding, empathy, and Sikh history literacy —
            helping students better understand the communities, histories, and
            stories around them.
          </p>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// 4. MIDDLE / HIGH SCHOOL
// =====================================================================
const GradeLevelSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6980" data-mp="25qi30e1c2gc5">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6981" data-mp="2akuqcz5xkbt6">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="mb-14" data-id="element-6982" data-mp="1vvgar9brgy5c">
        <SectionHeading eyebrow="Grade Levels" title="Designed for" highlight="Middle School and High School Learners" intro="The experience is built for both middle school and high school student groups — strong visual storytelling, emotional accessibility, and meaningful historical entry points across age ranges." data-id="element-6983" data-mp="2thdq4xuugnd2" />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-6984" data-mp="185canbxs7gj3">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="relative border border-amber-400/20 p-7 sm:p-8 hover:border-amber-400/45 transition-colors" data-id="element-6985" data-mp="10k8rn0jgfc59">
          <div className="flex items-baseline justify-between mb-5" data-id="element-6986" data-mp="hvbsai979tg6">
            <School className="w-5 h-5 text-amber-400" data-id="element-6987" data-mp="cvz701xz8ufp" />
            <span className="font-mono text-amber-400/50 text-[10px] tracking-[0.25em]" data-id="element-6988" data-mp="ctct7kkhtip1">
              GRADES 6–8
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-6989" data-mp="mtdbd36mlsrn">
            Middle School
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-6990" data-mp="3v85hlfrrmls1">
            A vivid and memorable introduction to Sikh history, culture, and
            identity — built to spark curiosity and connection at an
            age-appropriate level.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="relative border border-amber-400/20 p-7 sm:p-8 hover:border-amber-400/45 transition-colors" data-id="element-6991" data-mp="ntnegx8z6mnm">
          <div className="flex items-baseline justify-between mb-5" data-id="element-6992" data-mp="4xnrk0uuu9aw">
            <GraduationCap className="w-5 h-5 text-amber-400" data-id="element-6993" data-mp="9tcn7t3r761x" />
            <span className="font-mono text-amber-400/50 text-[10px] tracking-[0.25em]" data-id="element-6994" data-mp="euacsqr3f2dg">
              GRADES 9–12
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-6995" data-mp="1lc6yhxhtb2xe">
            High School
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-6996" data-mp="1qb0a1kdjgjux">
            A deeper opportunity for reflection, discussion, leadership themes,
            and historical understanding — supporting humanities, history, and
            equity learning.
          </p>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// 5. WHAT STUDENTS GAIN
// =====================================================================
const WhatStudentsGainSection = () => {
  const items = [{
    icon: Compass,
    title: 'Stronger Identity & Belonging',
    body: 'A space where students see themselves and their communities reflected in the story.'
  }, {
    icon: Eye,
    title: 'Sikh History Experienced Vividly',
    body: 'Story-led, visual learning that makes history feel alive rather than abstract.'
  }, {
    icon: Heart,
    title: 'Cultural Understanding & Empathy',
    body: 'A clearer, more human view of Sikh history and the communities around them.'
  }, {
    icon: Sparkles,
    title: 'Engagement Beyond the Textbook',
    body: 'A learning format that feels modern, immersive, and memorable for students.'
  }, {
    icon: MessageCircle,
    title: 'Reflection &amp; Discussion',
    body: 'Meaningful prompts for in-class conversation, follow-up, and connection.'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6997" data-mp="2xkloxpcsilbt">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6998" data-mp="32lj741xsvfdb">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6999" data-mp="2ol8g8ywhq0s5">
          <SectionHeading eyebrow="Student Outcomes" title="What Students" highlight="Gain" data-id="element-7000" data-mp="qtw2mv2pzykk" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto" data-id="element-7001" data-mp="27bwff9wigw6v">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className={i === 4 ? 'lg:col-start-2' : ''} data-id="element-7002" data-mp="2l3t3n2v26lxz" data-mpi={i}>
              <PremiumCard {...it} data-id="element-7003" data-mp="2z3vishbzf19j" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 6. HOW SCHOOLS CAN PARTICIPATE
// =====================================================================
const HowSchoolsParticipateSection = () => {
  const ways = [{
    icon: Users,
    title: 'School Group Visits',
    body: 'Organized middle school and high school visits to the immersive experience, structured around group size and learning goals.'
  }, {
    icon: Eye,
    title: 'Educator Preview Sessions',
    body: 'Preview opportunities for school leaders, curriculum teams, and educators to experience the program before bringing students.'
  }, {
    icon: BookOpen,
    title: 'Curriculum Enrichment',
    body: 'A meaningful experience that complements Sikh history, heritage learning, and broader multicultural education.'
  }, {
    icon: ShieldCheck,
    title: 'Pilot Partnerships',
    body: 'Early partnership opportunities for schools and education leaders interested in exploring access, fit, and scale.'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-7004" data-mp="19h6rme6uahk9">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/5 to-transparent pointer-events-none" data-id="element-7005" data-mp="1hy63bqnj4zel" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-7006" data-mp="1m2dqvki949b8">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-7007" data-mp="181ksgootwu2u">
          <SectionHeading eyebrow="Participation" title="How Schools" highlight="Can Participate" data-id="element-7008" data-mp="25oa4lrd8wn2f" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-7009" data-mp="1vy8eyv58uwle">
          {ways.map((w, i) => <motion.div key={w.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="relative border border-amber-400/15 hover:border-amber-400/45 transition-colors p-7 flex gap-5 bg-gradient-to-br from-amber-950/5 to-transparent" data-id="element-7010" data-mp="2jnsybf5b7vv7" data-mpi={i}>
              <span className="font-mono text-amber-400/40 text-xs tracking-[0.2em] shrink-0 pt-1" data-id="element-7011" data-mp="2fd76qjo32lx8">
                0{i + 1}
              </span>
              <div className="flex-1" data-id="element-7012" data-mp="25mm32htjyr7g">
                <div className="flex items-center gap-3 mb-3" data-id="element-7013" data-mp="25nnpfig0r04h">
                  <w.icon className="w-4 h-4 text-amber-400" data-id="element-7014" data-mp="20nsyxg05ocjy" />
                  <h3 className="font-serif text-lg sm:text-xl text-white leading-tight" data-id="element-7015" data-mp="20pcef4dzmjko">
                    {w.title}
                  </h3>
                </div>
                <p className="text-white/60 text-[14px] sm:text-[15px] font-light leading-relaxed" data-id="element-7016" data-mp="1rnw4syuzylhi">
                  {w.body}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 7. CURRICULUM CONNECTION
// =====================================================================
const CurriculumConnectionSection = () => {
  const themes = ['Sikh history and heritage', 'Leadership and empire', 'Culture, art, and memory', 'Identity and continuity', 'Belonging and representation', 'Historical empathy', 'Cross-cultural learning'];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-7017" data-mp="3tjljnqoh5uad">
      <div className="container mx-auto px-4 relative z-10" data-id="element-7018" data-mp="27adczyuuszp">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-7019" data-mp="30w98ecnrl8wd">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="lg:col-span-3" data-id="element-7020" data-mp="35u0xz00xsixe">
            <Eyebrow data-id="element-7021" data-mp="1gdx8mee51yvf">Curriculum &amp; Learning</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-7022" data-mp="1gcdsqowe9pu6">
              Built for Enrichment, Reflection, and{' '}
              <span className="text-amber-400" data-id="element-7023" data-mp="1q2ovw9897v00">Deeper Learning</span>
            </h2>
            <div className="w-16 h-px bg-amber-400/30 mb-6" data-id="element-7024" data-mp="243j5gv71g209" />
            <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed mb-5" data-id="element-7025" data-mp="201o7o0obg6r8">
              Life of the Sikh Kingdoms is designed to support heritage
              learning, Sikh history exploration, identity-based reflection, and
              broader multicultural understanding.
            </p>
            <p className="text-white/55 text-sm sm:text-base font-light leading-relaxed" data-id="element-7026" data-mp="3rpcea39kguqr">
              It is intended to complement school and community learning by
              making history feel vivid, human, and memorable — designed to{' '}
              <span className="text-amber-400/90" data-id="element-7027" data-mp="3hyrjys0xecdn">support</span>,{' '}
              <span className="text-amber-400/90" data-id="element-7028" data-mp="3mxkrm3tj795w">complement</span>, and{' '}
              <span className="text-amber-400/90" data-id="element-7029" data-mp="381d4ovqqntmx">enrich</span> existing
              programming.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="lg:col-span-2 relative border border-amber-400/20 bg-amber-950/5 p-6 sm:p-7" data-id="element-7030" data-mp="3d0zh52yqt5uo">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-7031" data-mp="ztzwvbc6zah8" />
            <Eyebrow data-id="element-7032" data-mp="vqbnqo7ww6tv">Learning Themes</Eyebrow>
            <ul className="mt-5 space-y-3" data-id="element-7033" data-mp="qss75hnqrhyr">
              {themes.map((t, i) => <li key={t} className="flex items-start gap-3 text-white/70 text-sm font-light" data-id="element-7034" data-mp="qumv74ub7q72" data-mpi={i}>
                  <span className="font-mono text-amber-400/60 text-[10px] tracking-[0.25em] mt-1 shrink-0" data-id="element-7035" data-mp="1jgfrcvl3o6kv">
                    0{i + 1}
                  </span>
                  <span data-id="element-7036" data-mp="19rec57mdpt7y">{t}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// 8. DIGITAL EXTENSION (kept short & secondary)
// =====================================================================
const DigitalExtensionSection = () => <section className="py-20 md:py-24 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-7037" data-mp="1xffd9vh8a7dc">
    <div className="container mx-auto px-4 relative z-10" data-id="element-7038" data-mp="24urbbp9eeit2">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="max-w-3xl mx-auto text-center" data-id="element-7039" data-mp="29rzyy5dbff1p">
        <Eyebrow data-id="element-7040" data-mp="2npesnmalq8zh">Optional Extension</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-5 mb-5 leading-[1.15] tracking-tight" data-id="element-7041" data-mp="2spkbqe2t2n5y">
          Optional Digital Engagement{' '}
          <span className="text-amber-400" data-id="element-7042" data-mp="32g5k2t8a7622">Beyond the Visit</span>
        </h2>
        <div className="w-12 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-7043" data-mp="32i8857pvip45" />
        <p className="text-white/60 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-7044" data-mp="37g9q1pbkcie1">
          An optional digital engagement layer can help students continue
          exploring Sikh history after the visit — through guided discovery,
          participation, and contemporary forms of engagement. Available as a
          supportive extension to the core in-person experience, not a
          replacement for it.
        </p>
        <div className="mt-7 inline-flex items-center gap-2 px-4 py-1.5 border border-amber-400/20 text-amber-400/70 text-[10px] tracking-[0.3em] uppercase" data-id="element-7045" data-mp="aihifowvvgpp">
          <Layers className="w-3 h-3" data-id="element-7046" data-mp="3e2c4pzj7gfyf" />
          Optional · Secondary to In-Person Experience
        </div>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// 9. COMMUNITY / MENTORSHIP
// =====================================================================
const CommunityMentorshipSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-7047" data-mp="2vat1psp6l1we">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-7048" data-mp="2qbznfy22l6v6" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-7049" data-mp="15svvkyy22h8p">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="max-w-4xl mx-auto text-center" data-id="element-7050" data-mp="1ak45rx77m6mx">
        <Eyebrow data-id="element-7051" data-mp="1ol86wb6ossuz">Beyond the Classroom</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-7052" data-mp="1oic8klqxg260">
          Learning That Can Extend{' '}
          <span className="text-amber-400" data-id="element-7053" data-mp="1fip57uen0mab">Beyond the Experience</span>
        </h2>
        <div className="w-16 h-px bg-amber-400/30 mx-auto mb-7" data-id="element-7054" data-mp="1khsbtja9n0ke" />
        <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed" data-id="element-7055" data-mp="44bln1qx2t03">
          This work can also open the door to broader community connection,
          mentorship, and intergenerational learning. It creates opportunities
          for students to encounter history not only as content, but as
          something connected to people, memory, and cultural leadership in the
          present.
        </p>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// 10. WHO WE WORK WITH
// =====================================================================
const WhoWeWorkWithSection = () => {
  const audiences = [{
    icon: Compass,
    label: 'Curriculum Leaders'
  }, {
    icon: Building2,
    label: 'Principals &amp; Vice Principals'
  }, {
    icon: BookOpen,
    label: 'Educators'
  }, {
    icon: ShieldCheck,
    label: 'Trustees'
  }, {
    icon: Heart,
    label: 'Equity &amp; Inclusion Teams'
  }, {
    icon: Users,
    label: 'Student Engagement Teams'
  }, {
    icon: Globe,
    label: 'Community Education Partners'
  }, {
    icon: GraduationCap,
    label: 'Sikh &amp; Punjabi Education Advocates'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-7056" data-mp="1tdsvvbdxjm4f">
      <div className="container mx-auto px-4 relative z-10" data-id="element-7057" data-mp="1pbg1tk8jpcnh">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-7058" data-mp="1pe28zirixq56">
          <SectionHeading eyebrow="Our Partners" title="Who We" highlight="Hope to Work With" data-id="element-7059" data-mp="15ngoq2qidtew" />
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-5xl mx-auto" data-id="element-7060" data-mp="11kad0fkmna11">
          {audiences.map((a, i) => <motion.div key={a.label} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="relative border border-amber-400/15 hover:border-amber-400/40 bg-gradient-to-br from-amber-950/5 to-transparent p-5 transition-colors group" data-id="element-7061" data-mp="rnnsjrw7qkzj" data-mpi={i}>
              <a.icon className="w-4 h-4 text-amber-400/70 group-hover:text-amber-400 mb-3 transition-colors" data-id="element-7062" data-mp="15qpcmemd41wn" />
              <p className="font-serif text-[15px] sm:text-base text-white leading-snug" dangerouslySetInnerHTML={{
            __html: a.label
          }} data-id="element-7063" data-mp="r089mqu2x3xn" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 11. CALL TO ACTION
// =====================================================================
const SchoolsCTASection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0.6]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-24 md:py-32 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-7064" data-mp="2q86q79kmx8hs">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.12),_transparent_60%)] pointer-events-none" data-id="element-7065" data-mp="2lbgf70not338" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-7066" data-mp="2v68c029g3lwy">
        <div className="max-w-3xl mx-auto text-center" data-id="element-7067" data-mp="einq0lrd8spu">
          <Eyebrow data-id="element-7068" data-mp="eh2uastbf3qe">Take the Next Step</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-7 leading-[1.05] tracking-tight" data-id="element-7069" data-mp="ah2ep4uhcmhr">
            Bring Sikh History{' '}
            <span className="text-amber-400" data-id="element-7070" data-mp="15sto8qn7vldt">to Life for Students</span>
          </h2>
          <div className="w-20 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-7071" data-mp="19tlvjecj3g7g" />
          <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-7072" data-mp="1erdwsyz2ceob">
            We are looking to work with schools, curriculum partners, and
            education leaders who want to create more meaningful, engaging, and
            culturally grounded learning experiences for middle school and high
            school students.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-7073" data-mp="1xionbf0vmpfo">
            <a href="#interest-form" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-7074" data-mp="22h883c3qhv6g">
              Request a School Conversation
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-7075" data-mp="twxfyso2j9r" />
            </a>
            <a href="#interest-form" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-7076" data-mp="3s0q25cmo50j1">
              Invite Us to Present
            </a>
          </div>
        </div>
      </div>
    </motion.section>;
};
// =====================================================================
// 12. INTEREST FORM
// =====================================================================
const InterestFormSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    school: '',
    role: '',
    grade: 'both',
    email: '',
    phone: '',
    interest: '',
    conversation: 'preview',
    comments: ''
  });
  const update = (k: string, v: string) => setForm(prev => ({
    ...prev,
    [k]: v
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const Field = ({
    label,
    children,
    optional = false
  }: {
    label: string;
    children: React.ReactNode;
    optional?: boolean;
  }) => <label className="block" data-id="element-7077" data-mp="28zfo8fz2bugn">
      <span className="text-amber-400/70 text-[10px] tracking-[0.3em] uppercase mb-2 block" data-id="element-7078" data-mp="28z5wonyddf8u">
        {label}
        {optional && <span className="text-white/30 normal-case tracking-normal ml-2" data-id="element-7079" data-mp="2czg3u3xwebpa">
            (optional)
          </span>}
      </span>
      {children}
    </label>;
  const inputCls = 'w-full bg-black border border-amber-400/20 px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-amber-400/60 transition-colors';
  return <section id="interest-form" className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-7080" data-mp="286i1zfs898q">
      <div className="container mx-auto px-4 relative z-10" data-id="element-7081" data-mp="1mp7bpr8hpzfe">
        <div className="max-w-3xl mx-auto" data-id="element-7082" data-mp="1hu0b4saipaup">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-12" data-id="element-7083" data-mp="1whczeorpluos">
            <Eyebrow data-id="element-7084" data-mp="20laxqx97n1gs">Get in Touch</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-tight" data-id="element-7085" data-mp="2fgh79u7iliev">
              Share Your <span className="text-amber-400" data-id="element-7086" data-mp="2aifkozavr0yn">Interest</span>
            </h2>
            <div className="w-16 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-7087" data-mp="l1emuaxboqit" />
            <p className="text-white/55 text-sm sm:text-base font-light max-w-xl mx-auto leading-relaxed" data-id="element-7088" data-mp="g33de1axwi14">
              Tell us about your school or organization. We'll follow up with
              the right next step — whether that's a preview, a presentation, or
              a partnership conversation.
            </p>
          </motion.div>

          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="relative border border-amber-400/40 bg-amber-950/10 p-10 sm:p-12 text-center" data-id="element-7089" data-mp="41txpkozfeev">
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-7090" data-mp="3hgzzifujdowe" />
              <div className="w-12 h-12 mx-auto border border-amber-400/50 bg-amber-400/10 flex items-center justify-center mb-5" data-id="element-7091" data-mp="wp69r0xio3tx">
                <Send className="w-5 h-5 text-amber-400" data-id="element-7092" data-mp="10of344on9zoy" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-3" data-id="element-7093" data-mp="2l6zdfo6x82xx">
                Thank you — we'll be in touch
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed max-w-md mx-auto" data-id="element-7094" data-mp="2h78nfmp7xqug">
                Your interest has been received. A member of the school
                partnerships team will reach out shortly to set up the right
                next conversation.
              </p>
            </motion.div> : <motion.form onSubmit={handleSubmit} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative border border-amber-400/20 bg-gradient-to-b from-amber-950/8 to-transparent p-7 sm:p-10 space-y-6" data-id="element-7095" data-mp="1xipucu13dtah">
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-7096" data-mp="2x0ngrjmbyii3" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-7097" data-mp="2n0wn1k5lg7j2">
                <Field label="Name" data-id="element-7098" data-mp="2n16d17qn2eor">
                  <input type="text" required value={form.name} onChange={e => update('name', e.target.value)} placeholder="Your full name" className={inputCls} data-id="element-7099" data-mp="2izkv37z8za7p" />
                </Field>
                <Field label="Role" data-id="element-7100" data-mp="35ypdhmz16ozk">
                  <input type="text" required value={form.role} onChange={e => update('role', e.target.value)} placeholder="e.g. Principal, Curriculum Lead" className={inputCls} data-id="element-7101" data-mp="35xo21he3s0hb" />
                </Field>
              </div>

              <Field label="School / Organization" data-id="element-7102" data-mp="1g7qoiy8nxts8">
                <input type="text" required value={form.school} onChange={e => update('school', e.target.value)} placeholder="School or district name" className={inputCls} data-id="element-7103" data-mp="1g91qcsijsrgp" />
              </Field>

              <Field label="Grade Level of Interest" data-id="element-7104" data-mp="1z630ssuvfjs1">
                <div className="flex flex-wrap gap-2" data-id="element-7105" data-mp="1z5b5lj56ro2a">
                  {[{
                v: 'middle',
                l: 'Middle School'
              }, {
                v: 'high',
                l: 'High School'
              }, {
                v: 'both',
                l: 'Both'
              }].map(opt => {
                const active = form.grade === opt.v;
                return <button key={opt.v} type="button" onClick={() => update('grade', opt.v)} className={`px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-semibold' : 'border-amber-400/25 text-white/60 hover:border-amber-400/60 hover:text-white'}`} data-id="element-7106" data-mp="ms4k9nbayenc">
                        {opt.l}
                      </button>;
              })}
                </div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-7107" data-mp="1fhtmg3zr0kqj">
                <Field label="Email" data-id="element-7108" data-mp="1aigzzkcshfoh">
                  <input type="email" required value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@school.org" className={inputCls} data-id="element-7109" data-mp="1ajiexzwxembh" />
                </Field>
                <Field label="Phone" optional data-id="element-7110" data-mp="1rskw0j2gefzi">
                  <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (___) ___-____" className={inputCls} data-id="element-7111" data-mp="1wrfcvfgv1ugz" />
                </Field>
              </div>

              <Field label="Area of Interest" data-id="element-7112" data-mp="1909x10ax726o">
                <input type="text" value={form.interest} onChange={e => update('interest', e.target.value)} placeholder="e.g. heritage learning, equity programming, school visit" className={inputCls} data-id="element-7113" data-mp="193o0ycz8fjg8" />
              </Field>

              <Field label="What would you like first?" data-id="element-7114" data-mp="2tgmyenhhc3kw">
                <div className="flex flex-wrap gap-2" data-id="element-7115" data-mp="2tf24n02zooo3">
                  {[{
                v: 'preview',
                l: 'Educator Preview'
              }, {
                v: 'presentation',
                l: 'Presentation'
              }, {
                v: 'partnership',
                l: 'Partnership Conversation'
              }].map(opt => {
                const active = form.conversation === opt.v;
                return <button key={opt.v} type="button" onClick={() => update('conversation', opt.v)} className={`px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-semibold' : 'border-amber-400/25 text-white/60 hover:border-amber-400/60 hover:text-white'}`} data-id="element-7116" data-mp="fh8ozipc60fo">
                        {opt.l}
                      </button>;
              })}
                </div>
              </Field>

              <Field label="Comments" optional data-id="element-7117" data-mp="3dtd8deeory1s">
                <textarea rows={4} value={form.comments} onChange={e => update('comments', e.target.value)} placeholder="Anything you'd like us to know — context, timing, group size, etc." className={inputCls + ' resize-none'} data-id="element-7118" data-mp="3ip2g450v0l96" />
              </Field>

              <button type="submit" className="group w-full inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.25)]" data-id="element-7119" data-mp="2zzm1yew94uaa">
                <Mail className="w-4 h-4" data-id="element-7120" data-mp="2cajlvbh9n9hw" />
                Send to School Partnerships
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-7121" data-mp="2h8uwvpjarq1p" />
              </button>

              <p className="text-white/35 text-[10px] tracking-[0.2em] uppercase text-center" data-id="element-7122" data-mp="297dgc2xk4weh">
                We'll respond within 3 business days
              </p>
            </motion.form>}
        </div>
      </div>
    </section>;
};
// =====================================================================
// PAGE
// =====================================================================
export function SchoolsPage() {
  return <LanguageProvider data-id="element-7123" data-mp="1qbnd6cdw1tz0">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-7124" data-mp="1lapve0wsj1y1">
        <ScrollProgress data-id="element-7125" data-mp="1lckj1mzg585t" />
        <Navbar data-id="element-7126" data-mp="207gxihxumrrc" />
        <main className="w-full" data-id="element-7127" data-mp="2089yf2a4mn1k">
          <SchoolsHero data-id="element-7128" data-mp="209bi1v57khuw" />
          <WhyThisMattersSection data-id="element-7129" data-mp="205dxr66w9k9f" />
          <WhoItServesSection data-id="element-7130" data-mp="1v84bfzesxopt" />
          <GradeLevelSection data-id="element-7131" data-mp="1v72niucot7qo" />
          <WhatStudentsGainSection data-id="element-7132" data-mp="1vb0axs6aslgd" />
          <HowSchoolsParticipateSection data-id="element-7133" data-mp="3bm4913q6d29w" />
          <CurriculumConnectionSection data-id="element-7134" data-mp="3bl2lw0vvwpbt" />
          <DigitalExtensionSection data-id="element-7135" data-mp="36o2osfh0iwwu" />
          <CommunityMentorshipSection data-id="element-7136" data-mp="36n119bit8hy8" />
          <WhoWeWorkWithSection data-id="element-7137" data-mp="36q5cutylufyx" />
          <SchoolsCTASection data-id="element-7138" data-mp="3e6seonk7zz5r" />
          <InterestFormSection data-id="element-7139" data-mp="3e5qthq9hpwab" />
        </main>
        <Footer data-id="element-7140" data-mp="3j6er4us85bdi" />
      </div>
    </LanguageProvider>;
}